import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { PayeeButtonComponent } from '../../payee-buttons/payee-button/payee-button.component';
import { PayeeIconButtonComponent } from '../../payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeFormFieldComponent } from '../../payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeDropzoneComponent } from '../../payee-dropzone/payee-dropzone.component';
import { PayeeIconComponent } from '../../payee-icon/payee-icon.component';
import { PayeePopupComponent } from '../../payee-popup/payee-popup.component';
import { PayeeWorkflowProvidedMediaComponent } from '../../payee-workflow-provided-media/payee-workflow-provided-media.component';
import {
  RunTransition,
  RunTransitionWithDialog,
  WorkflowService,
} from '../../services/workflow/workflow.service';
import { minTotalFilesIncludingProvidedMedia } from '../workflow-file-upload-validators';

const REQUIRED_REFUND_FILE_COUNT = 2;

type RefundAmount = {
  label: string;
  amount: number;
  currency: string;
};

type RefundTotal = {
  amount: number;
  currency: string;
};

type RefundDialogData = {
  transition_label: string;
  case_reference_id: string;
  fees: RefundAmount[];
  totals: RefundTotal[];
};

@Component({
  selector: 'app-refund-dialog',
  templateUrl: './refund-dialog.component.html',
  styleUrls: ['./refund-dialog.component.scss'],
  imports: [
    CurrencyPipe,
    DecimalPipe,
    FormsModule,
    MatProgressSpinner,
    PayeeButtonComponent,
    PayeeDropzoneComponent,
    PayeeFormFieldComponent,
    PayeeIconButtonComponent,
    PayeeIconComponent,
    PayeePopupComponent,
    PayeeWorkflowProvidedMediaComponent,
    ReactiveFormsModule,
    TranslateModule,
  ],
})
export class RefundDialogComponent implements OnInit {
  readonly data = inject<RunTransitionWithDialog>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<RefundDialogComponent>>(MatDialogRef);
  private readonly workflowService = inject(WorkflowService);
  private readonly fb = inject(FormBuilder);

  readonly dataLoading = signal(false);
  loading = false;
  dialogData?: RefundDialogData;

  readonly form = this.fb.group({
    files: this.fb.control<File[]>([], {
      validators: [
        minTotalFilesIncludingProvidedMedia(
          this.data.media,
          REQUIRED_REFUND_FILE_COUNT,
        ),
        Validators.maxLength(10),
      ],
      nonNullable: true,
    }),
    refund_amounts: this.fb.group({}),
  });

  get files() {
    return this.form.controls.files;
  }

  get refundAmounts() {
    return this.form.controls.refund_amounts;
  }

  readonly refundAmountControl = (currency: string) =>
    this.refundAmounts.get(currency);

  get dropzoneTitle(): string {
    return this.dialogData?.transition_label ?? '';
  }

  async ngOnInit(): Promise<void> {
    this.dataLoading.set(true);

    try {
      this.dialogData =
        await this.workflowService.getDialogData<RefundDialogData>(this.data);
      this.initRefundAmounts();
    } catch (error) {
      this.workflowService.showTransitionError(error);
      this.dialogRef.close();
    } finally {
      this.dataLoading.set(false);
    }
  }

  onDrop(droppedFiles: FileList): void {
    const fileValues = [...this.files.value];
    for (let i = 0; i < droppedFiles.length && fileValues.length < 10; ++i) {
      fileValues.push(droppedFiles.item(i) as File);
    }
    this.files.setValue(fileValues);
    this.files.markAsDirty();
    this.files.updateValueAndValidity();
  }

  removeFile(index: number): void {
    const fileValues = [...this.files.value];
    fileValues.splice(index, 1);
    this.files.setValue(fileValues);
    this.files.markAsDirty();
    this.files.updateValueAndValidity();
  }

  readonly onSubmit = async (): Promise<void> => {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    const uploadedFiles = this.files.value;
    const mediaUuids = (this.data.media ?? []).map((m) => m.uuid);
    const allFiles = [...mediaUuids, ...uploadedFiles];

    const payload: RunTransition = {
      caseId: this.data.caseId,
      transitionId: this.data.transitionId,
      params: {
        ...(this.data.extraParams ?? {}),
        files: allFiles,
        refund_amounts: this.getRefundAmounts(),
      },
    };

    this.loading = true;
    try {
      await this.workflowService.runTransition(payload);
      this.dialogRef.close(payload);
    } catch (error) {
      this.workflowService.showTransitionError(error);
    } finally {
      this.loading = false;
    }
  };

  readonly onCancel = (): void => {
    this.dialogRef.close();
  };

  private initRefundAmounts(): void {
    if (!this.dialogData) {
      return;
    }

    for (const total of this.dialogData.totals) {
      this.refundAmounts.addControl(
        total.currency,
        this.fb.control(total.amount, {
          nonNullable: true,
          validators: [
            Validators.required,
            Validators.min(1),
            Validators.max(total.amount),
          ],
        }),
      );
    }
  }

  private getRefundAmounts(): { currency: string; amount: number }[] {
    return (this.dialogData?.totals ?? []).map((total) => ({
      currency: total.currency,
      amount: this.refundAmounts.get(total.currency)?.value ?? total.amount,
    }));
  }
}
